/**
 * @fileoverview A faixa do consumo das mensagens do mês, para quem cuida do
 * plano (billing.manage, no layout): aparece em 80, 90 e 100% da franquia
 * comum ou da IA, só com os limites do plano ligados. Fechada, volta só no
 * próximo nível ou no próximo ciclo. Relê o consumo quando o servidor avisa
 * (um nível cruzado, ou o super mudou o plano), quando o socket volta e quando
 * o ciclo renova.
 */

import React, { useCallback, useContext, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Gauge, X } from "lucide-react";

import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";
import { i18n } from "../../translate/i18n";

const DAY_MS = 24 * 60 * 60 * 1000;

// O mesmo nível do servidor (alertLevelFor): 80, 90 ou 100% do limite.
const levelOf = ({ used, limit }) => {
  if (limit == null) return 0;
  if (limit <= 0) return 100;
  const share = used / limit;
  if (share >= 1) return 100;
  if (share >= 0.9) return 90;
  return share >= 0.8 ? 80 : 0;
};

const dismissKey = (companyId, usage, origin, level) => `messageUsageBanner:${companyId}:${usage.startsAt}:${origin}:${level}`;

const isDismissed = (key) => {
  try {
    return localStorage.getItem(key) === "1";
  } catch (err) {
    return false;
  }
};

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    bar: {
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "8px 10px 8px 20px",
      fontSize: 13,
      fontWeight: 500,
      lineHeight: 1.45,
      borderBottom: "1px solid transparent",
      // O fundo já entra sólido (semitransparente, o fundo claro do app
      // aparecia por trás no tema escuro); só o conteúdo entra suave.
      "& > *": { animation: "$in 180ms ease-out" },
      "@media (prefers-reduced-motion: reduce)": { "& > *": { animation: "none" } },
      [theme.breakpoints.down("sm")]: { padding: "8px 6px 8px 16px", fontSize: 12.5 },
    },
    near: dark
      ? { background: "#221a0a", color: "#FDE68A", borderBottomColor: "rgba(251,191,36,0.16)" }
      : { background: "#FFFBEB", color: "#78350F", borderBottomColor: "#FDE68A" },
    full: dark
      ? { background: "#241111", color: "#FECACA", borderBottomColor: "rgba(248,113,113,0.18)" }
      : { background: "#FEF2F2", color: "#7F1D1D", borderBottomColor: "#FECACA" },
    icon: { flexShrink: 0 },
    text: { flex: 1, minWidth: 0, fontVariantNumeric: "tabular-nums" },
    close: {
      width: 30,
      height: 30,
      flexShrink: 0,
      border: "none",
      borderRadius: 8,
      background: "transparent",
      color: "inherit",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      opacity: 0.7,
      transition: "opacity 150ms, background 150ms",
      "&:hover": { opacity: 1, background: dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)" },
      "&:focus-visible": { outline: "1px solid currentColor", opacity: 1 },
    },
    "@keyframes in": {
      from: { opacity: 0, transform: "translateY(-4px)" },
      to: { opacity: 1, transform: "none" },
    },
  };
});

const MessageUsageBanner = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const socketManager = useContext(SocketContext);
  const companyId = user?.companyId;
  const [usage, setUsage] = useState(null);
  const [closedKeys, setClosedKeys] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/account/message-usage");
      setUsage(data);
    } catch (err) {
      // A faixa é acessória: sem o consumo, ela não aparece.
    }
  }, []);

  useEffect(() => {
    if (!companyId) return undefined;
    load();
    const socket = socketManager.getSocket(companyId);
    socket.on(`company-${companyId}-message-usage`, load);
    socket.on("ready", load);
    return () => {
      socket.off(`company-${companyId}-message-usage`, load);
      socket.off("ready", load);
    };
  }, [companyId, socketManager, load]);

  // Relê quando o ciclo renova (o timer espera no máximo um dia por vez).
  useEffect(() => {
    if (!usage?.endsAt) return undefined;
    const wait = Math.min(Math.max(new Date(usage.endsAt).getTime() - Date.now() + 5000, 5000), DAY_MS);
    const timer = setTimeout(load, wait);
    return () => clearTimeout(timer);
  }, [usage, load]);

  if (!usage || usage.mode !== "enforce") return null;
  // O nível mais alto ainda não fechado; empatados, a franquia comum primeiro.
  const current = ["common", "ai"]
    .map((origin) => {
      const level = levelOf(usage[origin]);
      return { origin, level, key: dismissKey(companyId, usage, origin, level), ...usage[origin] };
    })
    .filter((item) => item.level && !closedKeys.includes(item.key) && !isDismissed(item.key))
    .sort((a, b) => b.level - a.level)[0];
  if (!current) return null;

  const full = current.level === 100;
  const locale = i18n.language;
  const text = i18n.t(`messageUsageBanner.${current.origin}${full ? "Full" : "Near"}`, {
    percent: full ? 100 : Math.floor((current.used / current.limit) * 100),
    used: Number(current.used).toLocaleString(locale),
    limit: Number(current.limit).toLocaleString(locale),
    date: new Date(usage.endsAt).toLocaleDateString(locale, { day: "2-digit", month: "2-digit", timeZone: "America/Sao_Paulo" }),
  });

  const close = () => {
    try {
      localStorage.setItem(current.key, "1");
    } catch (err) {
      // Sem o armazenamento do navegador, a faixa fica fechada só até recarregar.
    }
    setClosedKeys((keys) => [...keys, current.key]);
  };

  return (
    <div className={`${classes.bar} ${full ? classes.full : classes.near}`} role="status">
      <Gauge size={16} className={classes.icon} aria-hidden="true" />
      <span className={classes.text}>{text}</span>
      <button type="button" className={classes.close} onClick={close} aria-label={i18n.t("messageUsageBanner.close")} title={i18n.t("messageUsageBanner.close")}>
        <X size={15} />
      </button>
    </div>
  );
};

export default MessageUsageBanner;
